import React, { useCallback, useEffect, useId, useState } from 'react';

/** Aligns with `media-down(lg)` in SCSS ($grid-breakpoints lg − 0.02px). */
const TOC_MOBILE_MAX = '(max-width: 1099.98px)';

export interface OnThisPageNavItem {
  id: string;
  label: string;
}

export interface OnThisPageNavProps {
  items: OnThisPageNavItem[];
  activeId: string | null;
  onNavigate: (id: string) => void;
}

const OnThisPageNav: React.FC<OnThisPageNavProps> = ({ items, activeId, onNavigate }) => {
  const reactId = useId().replace(/:/g, '');
  const panelId = `on-this-page-panel-${reactId}`;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(TOC_MOBILE_MAX).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(TOC_MOBILE_MAX);
    const sync = () => {
      setIsNarrow(mql.matches);
      if (!mql.matches) {
        setMobileOpen(false);
      }
    };
    sync();
    mql.addEventListener('change', sync);
    return () => mql.removeEventListener('change', sync);
  }, []);

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      e.preventDefault();
      onNavigate(id);
      if (isNarrow) {
        setMobileOpen(false);
      }
    },
    [onNavigate, isNarrow]
  );

  if (!items.length) return null;

  const list = (
    <ul className={`on-this-page-nav__list${isNarrow ? ' on-this-page-nav__list--in-mobile-panel' : ''}`}>
      {items.map((item) => {
        const isActive = activeId === item.id;
        return (
          <li key={item.id} className="on-this-page-nav__item">
            <a
              href={`#${item.id}`}
              className={`on-this-page-nav__link${isActive ? ' is-active' : ''}`}
              onClick={(e) => handleLinkClick(e, item.id)}
              aria-current={isActive ? 'true' : undefined}
            >
              {item.label}
            </a>
          </li>
        );
      })}
    </ul>
  );

  return (
    <nav className="on-this-page-nav" aria-label="On this page">
      {isNarrow ? (
        <>
          <button
            type="button"
            className="on-this-page-nav__mobile-toggle"
            aria-expanded={mobileOpen}
            aria-controls={panelId}
            onClick={() => setMobileOpen((o) => !o)}
          >
            <span className="on-this-page-nav__mobile-toggle-label">Jump to section</span>
            <span className="on-this-page-nav__mobile-toggle-state">{mobileOpen ? 'Hide' : 'Show'}</span>
          </button>
          <div
            id={panelId}
            className="on-this-page-nav__mobile-panel"
            hidden={!mobileOpen}
          >
            {list}
          </div>
        </>
      ) : (
        <>
          {list}
        </>
      )}
    </nav>
  );
};

export default OnThisPageNav;
